<template>
  <svg
    v-if="store.isEggEnabled('moonLogo')"
    class="hw-moon h-12 w-12"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="seasonal-logo-moon"
  >
    <defs>
      <linearGradient :id="`${uid}-sky`" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
        <stop offset="0" stop-color="#1C1342" />
        <stop offset="1" stop-color="#0E0A22" />
      </linearGradient>
      <radialGradient :id="`${uid}-halo`" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0.6" stop-color="#F28C28" stop-opacity="0.45" />
        <stop offset="1" stop-color="#F28C28" stop-opacity="0" />
      </radialGradient>
      <radialGradient :id="`${uid}-face`" cx="0.4" cy="0.35" r="0.72">
        <stop offset="0" stop-color="#FFF1D6" />
        <stop offset="0.65" stop-color="#FFC26B" />
        <stop offset="1" stop-color="#E8621A" />
      </radialGradient>
      <clipPath :id="`${uid}-clip`">
        <rect width="32" height="32" rx="9" />
      </clipPath>
    </defs>
    <rect width="32" height="32" rx="9" :fill="`url(#${uid}-sky)`" />
    <g :clip-path="`url(#${uid}-clip)`">
      <circle cx="6" cy="6.5" r="0.75" fill="#D9CCFF" opacity="0.6" />
      <circle cx="11" cy="3.5" r="0.5" fill="#D9CCFF" opacity="0.45" />
      <circle cx="4" cy="16" r="0.5" fill="#D9CCFF" opacity="0.4" />
      <circle cx="27.5" cy="26.5" r="0.55" fill="#D9CCFF" opacity="0.45" />
      <circle cx="9" cy="27" r="0.45" fill="#D9CCFF" opacity="0.35" />
      <g class="hw-moon__disc">
        <circle cx="18.5" cy="14" r="13" :fill="`url(#${uid}-halo)`" />
        <circle cx="18.5" cy="14" r="9" :fill="`url(#${uid}-face)`" />
        <circle cx="13.2" cy="10.6" r="1.3" fill="#D9742A" opacity="0.45" />
        <circle cx="24.6" cy="17.6" r="1.7" fill="#D9742A" opacity="0.4" />
        <circle cx="21.8" cy="7.9" r="0.8" fill="#D9742A" opacity="0.4" />
        <circle cx="14.4" cy="19.8" r="0.9" fill="#D9742A" opacity="0.35" />
        <g transform="translate(10.83 5.9)" fill="#5B1FD3">
          <path
            d="M7.68626 8.68484L6.35563 7.67335L5.88281 8.25907L7.75212 9.70497L11.3857 5.19165L10.4753 5.125L7.68626 8.68484Z"
          />
          <path
            d="M10.3804 7.56712C9.95309 9.03497 9.04745 10.2229 7.63606 11.1615C6.71159 10.6544 5.96107 9.88098 5.48212 8.94166C5.04616 8.09326 4.78583 7.04883 4.70585 5.82876C5.71725 5.69101 6.71075 5.44415 7.66899 5.09248C8.17396 5.28801 8.69033 5.45272 9.21525 5.58568L9.70845 4.9584C9.01261 4.80151 8.33117 4.58643 7.67134 4.31543C6.55086 4.76786 5.30569 5.05327 3.95703 5.19206C4.00016 8.29085 5.02812 10.7051 7.67134 11.9997C9.8049 10.7051 11.0924 8.86795 11.3433 6.3643L10.3804 7.56712Z"
          />
        </g>
      </g>
      <g class="hw-moon__bat">
        <path
          class="hw-moon__wings"
          d="M3 10.3c-.9-1.2-2.1-1.6-3-1.3.7.3 1 .9 1 1.5-.5.1-1 .5-1 .5s.9.2 1.2.9c.6-.2 1.2 0 1.6.5l.2.3.2-.3c.4-.5 1-.7 1.6-.5.3-.7 1.2-.9 1.2-.9s-.5-.4-1-.5c0-.6.3-1.2 1-1.5-.9-.3-2.1.1-3 1.3z"
          fill="#1C1342"
        />
      </g>
      <path d="M0 27.5c6-2.6 11-3.2 16-2.2s10 3.8 16 2.6V32H0z" fill="#120C2E" />
    </g>
  </svg>
  <div
    v-else-if="thanksgiving.isEggEnabled('turkeyLogo')"
    class="tg-logo"
    :class="turkeyClasses"
    :title="thanksgiving.pardoned ? 'Pardoned! Click for the proclamation.' : undefined"
    data-testid="seasonal-logo-turkey"
    @click="onTurkeyClick"
  >
    <ThanksgivingTurkey :moves="turkeyMoves" :pardoned="thanksgiving.pardoned" />
    <span v-if="thanksgiving.pardoned" class="tg-logo__pardon" data-testid="seasonal-logo-pardon-tag">Pardoned!</span>
    <template v-if="cheering">
      <span v-for="leaf in CHEER_LEAVES" :key="leaf.id" class="tg-logo__leaf" :style="leaf.style" />
    </template>
    <ThanksgivingPardon v-if="pardonOpen" @close="pardonOpen = false" />
  </div>
  <PulseCircleLogo v-else class="h-8 w-8" />
</template>

<script setup lang="ts">
import { useSeasonalThemeStore } from '~/store/seasonal-theme';
import { useThanksgivingThemeStore } from '~/store/thanksgiving-theme';

import { useSeasonalCelebrationsStore } from '~/store/seasonal-celebrations';

import ThanksgivingPardon from './ThanksgivingPardon.vue';
import ThanksgivingTurkey, { TurkeyMoves } from './ThanksgivingTurkey.vue';

const store = useSeasonalThemeStore();
const thanksgiving = useThanksgivingThemeStore();

// The turkey logo breaks into its dance routine now and then (the "Dancing turkey" egg) and gobbles when clicked.
const DANCE_FIRST_MS = [8_000, 18_000] as const;
const DANCE_EVERY_MS = [45_000, 90_000] as const;
const DANCE_MS = 6400;
const GOBBLE_MS = 1600;
const CHEER_MS = 1500;
// Leaves tossed up when a claim is resolved, each on its own arc.
const CHEER_LEAVES = [
  { colour: '#ea580c', dx: -26, dy: -30, spin: -200 },
  { colour: '#f59e0b', dx: -10, dy: -42, spin: 160 },
  { colour: '#b91c1c', dx: 8, dy: -38, spin: -120 },
  { colour: '#d97706', dx: 24, dy: -28, spin: 220 },
  { colour: '#c2410c', dx: 34, dy: -12, spin: -260 },
].map((leaf, id) => ({
  id,
  style: {
    '--tg-cheer-colour': leaf.colour,
    '--tg-cheer-dx': `${leaf.dx}px`,
    '--tg-cheer-dy': `${leaf.dy}px`,
    '--tg-cheer-spin': `${leaf.spin}deg`,
  },
}));

const celebrations = useSeasonalCelebrationsStore();
const dancing = ref(false);
const gobbling = ref(false);
const cheering = ref(false);
const pardonOpen = ref(false);
const timers = new Set<ReturnType<typeof setTimeout>>();

function later(fn: () => void, ms: number) {
  const timer = setTimeout(() => {
    timers.delete(timer);
    fn();
  }, ms);

  timers.add(timer);
}

function between([min, max]: readonly [number, number]): number {
  return min + Math.random() * (max - min);
}

const turkeyMoves = computed(() => {
  if (gobbling.value || cheering.value) return TurkeyMoves.GOBBLE;

  return dancing.value ? TurkeyMoves.DANCE : TurkeyMoves.IDLE;
});

const turkeyClasses = computed(() => ({
  'tg-logo--cheer': cheering.value,
  'tg-logo--dance': dancing.value && !gobbling.value && !cheering.value,
}));

function scheduleDance(range: readonly [number, number]) {
  later(() => {
    if (thanksgiving.isEggEnabled('dancingTurkey') && !dancing.value) {
      dancing.value = true;
      later(() => {
        dancing.value = false;
      }, DANCE_MS);
    }
    scheduleDance(DANCE_EVERY_MS);
  }, between(range));
}

function gobble() {
  if (gobbling.value) return;

  gobbling.value = true;
  later(() => {
    gobbling.value = false;
  }, GOBBLE_MS);
}

// A resolved or approved claim (raised by the claims client) gets a hop for joy and a toss of leaves.
watch(
  () => celebrations.claimsResolved,
  () => {
    if (!thanksgiving.isEggEnabled('claimCheers') || cheering.value) return;

    cheering.value = true;
    later(() => {
      cheering.value = false;
    }, CHEER_MS);
  },
);

// On pardon day a click opens the proclamation as well as the gobble.
function onTurkeyClick() {
  gobble();
  if (thanksgiving.pardoned) pardonOpen.value = true;
}

onMounted(() => scheduleDance(DANCE_FIRST_MS));
onBeforeUnmount(() => timers.forEach(clearTimeout));

// Gradient and clip ids are per instance: the mobile menu renders a second logo while the desktop one is
// display:none, and gradients inside a hidden SVG do not paint.
const uid = useId();
</script>
